import * as THREE from "three";
import { facet, mirrorX, seeded } from "@rpg/meshkit";
import type { BandPiece, GlyphRowPiece, GlyphShape, HoodPiece, LathePiece, Piece, ProfilePoint, RadialPiece, ShapeSpec, Surface, Vec3 } from "./card";

// The piece builders: each turns one card piece into faceted, vertex-coloured geometry in model space (the figure's
// rest pose), tagged with the joint it rides on. General mechanisms, ported from the look test's Wanderer page
// (engine/docs/evidence/002-look-test/wanderer-sheet.html): lathe shells with a front gap, bands, glyph rows, a hood
// fitted ring by ring to a face circle (0040), shaped primitives, radial repeats; chain links are in chain.ts.

/** One built piece: model-space geometry (position, normal, color, glow) and its joint. */
export interface Built {
  joint: string;
  geo: THREE.BufferGeometry;
}

/** What a builder needs: the colour lookup (palette keys or hex) and the piece's random source. */
export interface BuildContext {
  colour(c: string): THREE.Color;
  rng: () => number;
}

/** A piece's surface options for `facet`. */
export function surface(p: Surface, ctx: BuildContext, defaults: { jitter?: number; tint?: number } = {}) {
  const glow = p.glow ?? 0;
  return { colour: ctx.colour(p.colour), jitter: glow > 0 ? 0 : (p.jitter ?? defaults.jitter ?? 0), tint: glow > 0 ? 0 : (p.tint ?? defaults.tint ?? 0), glow, rng: ctx.rng };
}

/** The joint a mirrored copy lands on: `.l` ↔ `.r` (also mid-name, `arm.l.x`), else the same joint. */
export function mirrorJoint(joint: string): string {
  const parts = joint.split(".");
  const i = parts.findIndex((p) => p === "l" || p === "r");
  if (i < 0) return joint;
  parts[i] = parts[i] === "l" ? "r" : "l";
  return parts.join(".");
}

/** A three.js geometry for a shape spec, placed (at, rot, scale), before facets. */
export function shapeGeometry(s: ShapeSpec): THREE.BufferGeometry {
  const z = s.size;
  const at = (i: number, d: number): number => z[i] ?? d;
  const [ra, rb] = s.segments ?? [0, 0];
  const d = s.detail ?? 0;
  let g: THREE.BufferGeometry;
  switch (s.shape) {
    case "sphere":
      g = new THREE.SphereGeometry(at(0, 0.1), ra || 8, rb || 6);
      break;
    case "cone":
      g = new THREE.ConeGeometry(at(0, 0.1), at(1, 0.2), ra || 6, rb || 1);
      break;
    case "cylinder":
      g = new THREE.CylinderGeometry(at(0, 0.1), at(1, at(0, 0.1)), at(2, 0.2), ra || 6, rb || 1);
      break;
    case "box":
      g = new THREE.BoxGeometry(at(0, 0.1), at(1, at(0, 0.1)), at(2, at(0, 0.1)));
      break;
    case "icosahedron":
      g = new THREE.IcosahedronGeometry(at(0, 0.1), d);
      break;
    case "octahedron":
      g = new THREE.OctahedronGeometry(at(0, 0.1), d);
      break;
    case "dodecahedron":
      g = new THREE.DodecahedronGeometry(at(0, 0.1), d);
      break;
    case "tetrahedron":
      g = new THREE.TetrahedronGeometry(at(0, 0.1), d);
      break;
    case "torus":
      g = new THREE.TorusGeometry(at(0, 0.1), at(1, 0.02), rb || 4, ra || 8, at(2, Math.PI * 2));
      break;
  }
  if (s.flip) g.rotateX(Math.PI);
  const m = new THREE.Matrix4().compose(
    new THREE.Vector3(...(s.at ?? [0, 0, 0])),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(...(s.rot ?? [0, 0, 0]))),
    new THREE.Vector3(...(s.scale ?? [1, 1, 1])),
  );
  return g.applyMatrix4(m);
}

/** Linear interpolation along a profile [[r, y], ...] ordered by y. */
export function profileRadius(pts: readonly ProfilePoint[], y: number): number {
  if (y <= pts[0]![1]) return pts[0]![0];
  for (let i = 1; i < pts.length; i++)
    if (y <= pts[i]![1]) {
      const [r0, y0] = pts[i - 1]!;
      const [r1, y1] = pts[i]!;
      return y1 === y0 ? r1 : r0 + ((r1 - r0) * (y - y0)) / (y1 - y0);
    }
  return pts[pts.length - 1]![0];
}

/** Points above `from` move back (-z) by `back` per metre above it. */
function tipBack(g: THREE.BufferGeometry, tip: { from: number; back: number } | undefined): THREE.BufferGeometry {
  if (!tip) return g;
  const p = g.getAttribute("position");
  for (let i = 0; i < p.count; i++) {
    const y = p.getY(i);
    if (y > tip.from) p.setZ(i, p.getZ(i) - (y - tip.from) * tip.back);
  }
  return g;
}

function lathe(p: LathePiece): THREE.BufferGeometry {
  const gap = p.gap ?? 0;
  const g = new THREE.LatheGeometry(
    p.profile.map(([r, y]) => new THREE.Vector2(r, y)),
    p.segments,
    gap / 2,
    Math.PI * 2 - gap,
  );
  return tipBack(g, p.tip);
}

function band(p: BandPiece): THREE.BufferGeometry {
  const gap = p.gap ?? 0;
  return new THREE.CylinderGeometry(p.radius[1], p.radius[0], p.height, p.segments, 1, true, gap / 2, Math.PI * 2 - gap).translate(0, p.y, 0);
}

function glyphRow(p: GlyphRowPiece, ctx: BuildContext): THREE.BufferGeometry[] {
  const out: THREE.BufferGeometry[] = [];
  for (let i = 0; i < p.count; i++) {
    const a = (i / p.count) * Math.PI * 2;
    if (p.skip && Math.abs(Math.atan2(Math.sin(a), Math.cos(a))) < p.skip) continue;
    const t = new THREE.ConeGeometry(p.size * 0.6, p.size, 3).scale(1, 1, 0.12);
    t.rotateY(a).translate(Math.sin(a) * p.radius, p.y, Math.cos(a) * p.radius);
    out.push(facet(t, { ...surface(p, ctx), jitter: 0, tint: 0 }));
  }
  return out;
}

/**
 * The fitted hood (0040): rings along the profile, plus rings at even angles around the face circle, each ring's front
 * gap exactly the face's half-width at that height, so the opening is a round polygon and the edge meets the face all
 * the way round. Returns the geometry below `split.from` and above it (the sprung point), split on a shared ring.
 */
export function hoodGeometry(p: HoodPiece): { main: THREE.BufferGeometry; tip: THREE.BufferGeometry | null } {
  const { y: fy, radius: fr, rings: n } = p.face;
  const prof = p.profile;
  const around = Array.from({ length: n }, (_, k) => fy + fr * Math.sin(-Math.PI / 2 + (Math.PI * k) / (n - 1)));
  const all = [...prof.map(([, y]) => y), ...around, ...(p.rings ?? []), ...(p.split ? [p.split.from] : [])].filter((y) => y >= prof[0]![1] && y <= prof[prof.length - 1]![1]);
  all.sort((a, b) => a - b);
  const ys = all.filter((y, i) => i === 0 || y > all[i - 1]! + 1e-4);
  const seg = p.segments;
  const pos: number[] = [];
  for (const y of ys) {
    const r = profileRadius(prof, y);
    const dy = y - fy;
    const hw = dy * dy < fr * fr ? Math.sqrt(fr * fr - dy * dy) : 0;
    const gap = r > 1e-4 ? Math.asin(Math.min(0.98, hw / r)) : 0;
    for (let j = 0; j <= seg; j++) {
      const a = gap + ((Math.PI * 2 - 2 * gap) * j) / seg;
      pos.push(Math.sin(a) * r, y, Math.cos(a) * r);
    }
  }
  const cut = p.split?.from ?? Infinity;
  const main: number[] = [];
  const tip: number[] = [];
  for (let i = 0; i < ys.length - 1; i++)
    for (let j = 0; j < seg; j++) {
      const a = i * (seg + 1) + j;
      const b = a + seg + 1;
      (ys[i]! >= cut - 1e-6 ? tip : main).push(a, a + 1, b, a + 1, b + 1, b);
    }
  const make = (idx: number[]): THREE.BufferGeometry | null => {
    if (!idx.length) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos.slice(), 3));
    g.setIndex(idx);
    return tipBack(g, p.tip);
  };
  return { main: make(main)!, tip: make(tip) };
}

function radial(p: RadialPiece, ctx: BuildContext): THREE.BufferGeometry[] {
  const out: THREE.BufferGeometry[] = [];
  const [s0, s1] = p.scaleRange ?? [1, 1];
  const rr = p.rotRange ?? [0, 0, 0];
  for (let i = 0; i < p.count; i++) {
    const a = (i / p.count) * Math.PI * 2;
    const k = s0 + (s1 - s0) * ctx.rng();
    const g = shapeGeometry(p.item);
    g.scale(k, k, k);
    g.applyMatrix4(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler((ctx.rng() * 2 - 1) * rr[0], (ctx.rng() * 2 - 1) * rr[1], (ctx.rng() * 2 - 1) * rr[2])));
    g.rotateY(a).translate(Math.sin(a) * p.radius, p.y, Math.cos(a) * p.radius);
    out.push(facet(g, surface(p, ctx)));
  }
  return out;
}

/** A glyph's flat geometry for a chain link, centred, in the link's x-y plane. */
export function glyphGeometry(shape: GlyphShape, size: number): THREE.BufferGeometry[] {
  const s = size / 0.06; // authored at 6 cm
  const box = (w: number, h: number, x: number, y: number) => new THREE.BoxGeometry(w * s, h * s, 0.004).translate(x * s, y * s, 0);
  switch (shape) {
    case "triangle":
      return [new THREE.ConeGeometry(0.03 * s, 0.05 * s, 3).scale(1, 1, 0.1)];
    case "bars":
      return [box(0.06, 0.012, 0, 0.02), box(0.06, 0.012, 0, -0.02)];
    case "comb":
      return [box(0.012, 0.06, -0.024, 0), box(0.05, 0.01, 0.002, 0.024), box(0.05, 0.01, 0.002, 0), box(0.05, 0.01, 0.002, -0.024)];
    case "diamond":
      return [new THREE.OctahedronGeometry(0.026 * s, 0).scale(1, 1, 0.1), box(0.06, 0.008, 0, -0.035)];
  }
}

/** Build every geometry a piece makes (chain links excepted: chain.ts), each tagged with its joint. */
export function buildPiece(p: Piece, ctx: BuildContext): Built[] {
  let built: Built[];
  switch (p.kind) {
    case "lathe":
      built = [{ joint: p.joint, geo: facet(lathe(p), surface(p, ctx, { jitter: 0.008, tint: 0.08 })) }];
      break;
    case "band":
      built = [{ joint: p.joint, geo: facet(band(p), surface(p, ctx)) }];
      break;
    case "glyphRow":
      built = glyphRow(p, ctx).map((geo) => ({ joint: p.joint, geo }));
      break;
    case "hood": {
      const { main, tip } = hoodGeometry(p);
      const o = surface(p, ctx, { jitter: 0.003, tint: 0.05 });
      if (!tip || !p.split) {
        built = [{ joint: p.joint, geo: facet(main, o) }];
        break;
      }
      // jitter both halves as one, so the shared ring stays closed
      const whole = new THREE.BufferGeometry();
      const mi = main.getIndex()!.array;
      const ti = tip.getIndex()!.array;
      whole.setAttribute("position", main.getAttribute("position"));
      whole.setIndex([...mi, ...ti]);
      const f = facet(whole, o);
      const nMain = mi.length; // vertices, after toNonIndexed: one per index
      const tipGeo = slice(f, nMain, f.getAttribute("position").count);
      // the point bends from the cut rather than hinging there (the chain reads `bend`): 0 on the shared ring, so the
      // seam stays closed as the point swings, easing to 1 at the top
      const top = p.profile[p.profile.length - 1]![1];
      const tp = tipGeo.getAttribute("position");
      const bend = new Float32Array(tp.count);
      for (let i = 0; i < tp.count; i++) bend[i] = THREE.MathUtils.smoothstep((tp.getY(i) - p.split.from) / Math.max(1e-4, top - p.split.from), 0, 1);
      tipGeo.setAttribute("bend", new THREE.BufferAttribute(bend, 1));
      built = [
        { joint: p.joint, geo: slice(f, 0, nMain) },
        { joint: p.split.joint, geo: tipGeo },
      ];
      break;
    }
    case "shape":
      built = [{ joint: p.joint, geo: facet(shapeGeometry(p), surface(p, ctx)) }];
      break;
    case "radial":
      built = radial(p, ctx).map((geo) => ({ joint: p.joint, geo }));
      break;
    case "links":
      built = []; // built per link by chain.ts
      break;
  }
  if (p.mirror) built = built.concat(built.map((b) => ({ joint: mirrorJoint(b.joint), geo: mirrorX(b.geo) })));
  return built;
}

/** Vertices [from, to) of a non-indexed geometry, every attribute. */
export function slice(g: THREE.BufferGeometry, from: number, to: number): THREE.BufferGeometry {
  const out = new THREE.BufferGeometry();
  for (const [k, a] of Object.entries(g.attributes) as [string, THREE.BufferAttribute][]) {
    out.setAttribute(k, new THREE.BufferAttribute((a.array as Float32Array).slice(from * a.itemSize, to * a.itemSize), a.itemSize));
  }
  return out;
}

/** A seeded random source for a piece: the card seed, the piece's index and its own offset. */
export function pieceRng(cardSeed: number, index: number, own = 0): () => number {
  return seeded((cardSeed * 7919 + index * 104_729 + own * 31) | 0);
}

/** Vec3 to a three.js vector. */
export const v3 = (v: Vec3 | undefined, d: Vec3 = [0, 0, 0]): THREE.Vector3 => new THREE.Vector3(...(v ?? d));
